import React, { useState } from 'react'
import { App, Button, Space } from 'antd'
import { supabaseClient } from '../../lib/supabaseClient'
import { brand } from '../../theme/brand'

interface ActionDef {
  key: string
  label: string
  danger?: boolean
}

// Integrity levers, driven through the admin-gated `admin-action` Edge Function:
//   suspend/unsuspend  → Supabase Auth ban (auth.admin.updateUserById ban_duration)
//   stream_*           → GetStream chat ban / shadow-ban / (de)activate
const ACTIONS: ActionDef[] = [
  { key: 'suspend', label: 'Suspend', danger: true },
  { key: 'unsuspend', label: 'Unsuspend' },
  { key: 'stream_ban', label: 'Ban from chat', danger: true },
  { key: 'stream_shadow_ban', label: 'Shadow-ban' },
  { key: 'stream_deactivate', label: 'Deactivate chat', danger: true },
  { key: 'stream_reactivate', label: 'Reactivate chat' },
]

// Plain-language reference for each lever — what it does to the member when
// applied, and how to reverse it. Grouped by the part of the app it controls.
interface ActionHelp {
  title: string
  scope: 'Account' | 'Chat'
  body: string
}
const ACTION_HELP: ActionHelp[] = [
  {
    title: 'Suspend / Unsuspend',
    scope: 'Account',
    body: 'Suspend bans the member’s account (Supabase Auth, ~100-year ban). They are signed out and cannot sign back in on any device — a full lockout from the app. Unsuspend lifts the ban and restores normal access. Use Suspend for serious or repeated violations.',
  },
  {
    title: 'Ban from chat',
    scope: 'Chat',
    body: 'Bans the member in GetStream chat app-wide: they can no longer send messages in any channel — class chats or 1:1 DMs. Everything else in the app still works. (Reversible from Stream.)',
  },
  {
    title: 'Shadow-ban',
    scope: 'Chat',
    body: 'Silently bans the member in chat: their messages still appear to them but are hidden from everyone else, and they are not notified. Useful for de-escalating a disruptive member without tipping them off.',
  },
  {
    title: 'Deactivate chat / Reactivate chat',
    scope: 'Chat',
    body: 'Deactivate removes the member’s GetStream user entirely — they cannot connect to chat at all and drop out of every channel. Reactivate chat restores a previously-deactivated chat user. (More severe than a ban; the member effectively disappears from chat.)',
  },
]

/**
 * Per-user integrity actions. Each button calls the `admin-action` Edge Function
 * (which verifies the caller is an admin, performs the action with the service
 * role / Stream secret, and logs it to moderation_actions).
 */
export function ModerationActions({ userId }: { userId?: string }) {
  const { message } = App.useApp()
  const [busy, setBusy] = useState<string | null>(null)

  const run = async (action: string) => {
    if (!userId) return
    setBusy(action)
    try {
      const { error } = await supabaseClient.functions.invoke('admin-action', {
        body: { targetUserId: userId, action },
      })
      if (error) throw error
      message.success(`Applied "${action}"`)
    } catch (e) {
      message.error(`Failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <Space wrap>
        {ACTIONS.map((a) => (
          <Button
            key={a.key}
            danger={a.danger}
            loading={busy === a.key}
            disabled={!userId || (busy !== null && busy !== a.key)}
            onClick={() => run(a.key)}
          >
            {a.label}
          </Button>
        ))}
      </Space>

      {/* Reference: what each lever does to the member + how to reverse it. */}
      <div
        data-testid="integrity-actions-help"
        style={{
          marginTop: 18,
          border: '1px solid ' + brand.cardLine,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            fontFamily: brand.fSans,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: brand.sub,
            background: brand.paper,
            padding: '10px 14px',
          }}
        >
          What each action does
        </div>
        {ACTION_HELP.map((h, i) => (
          <div
            key={h.title}
            style={{
              padding: '12px 14px',
              borderTop: i === 0 ? 'none' : '1px solid ' + brand.cardLine,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 700,
                  fontSize: 13,
                  color: brand.ink,
                }}
              >
                {h.title}
              </span>
              <span
                style={{
                  fontFamily: brand.fSans,
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: '.04em',
                  textTransform: 'uppercase',
                  color: brand.sub,
                  border: '1px solid ' + brand.cardLine,
                  borderRadius: 6,
                  padding: '1px 6px',
                }}
              >
                {h.scope}
              </span>
            </div>
            <div
              style={{
                fontFamily: brand.fSans,
                fontSize: 12.5,
                lineHeight: 1.6,
                color: brand.sub,
              }}
            >
              {h.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
